/**
 * TaxTab — GST and HSN taxation fields — Tab 1 of the Item form.
 *
 * HSN is selected from the Tax Master; GST rate is derived and read-only —
 * the backend ignores any GST value sent by the client and re-derives it
 * from the selected HSN on save.
 *
 * Props:
 *   control        RHF control object
 *   errors         RHF errors object
 *   setValue       RHF setValue — used to sync gst_rate when HSN changes
 *   hsnValueRecord Tax Master record for the current hsn_code (edit mode; may
 *                  be a fallback stub if the live lookup failed/hasn't shipped)
 *   hsnWarning     'inactive' | 'unavailable' | null
 */
import React, { useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { Controller } from 'react-hook-form';
import SectionCard from '../SectionCard';
import HsnAutocomplete from '../HsnAutocomplete';
import { InvTextField } from '../InvField';

export default function TaxTab({ control, errors, setValue, hsnValueRecord = null, hsnWarning = null }) {
  const [description, setDescription] = useState(hsnValueRecord?.description || '');
  const [warning, setWarning] = useState(hsnWarning);

  useEffect(() => {
    setDescription(hsnValueRecord?.description || '');
    setWarning(hsnWarning);
  }, [hsnValueRecord, hsnWarning]);

  function warningHelperText() {
    if (warning === 'inactive') {
      return 'This HSN is now inactive in Tax Master — pick an active one to keep GST current.';
    }
    if (warning === 'unavailable') {
      return 'Could not verify this HSN right now — showing the previously saved value.';
    }
    return description || 'Search by HSN code or description';
  }

  return (
    <SectionCard title="Taxation">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name="hsn_code"
            control={control}
            rules={{ required: 'Select an HSN code' }}
            render={({ field }) => (
              <HsnAutocomplete
                value={field.value}
                valueRecord={hsnValueRecord}
                onChange={(hsnCode, record) => {
                  field.onChange(hsnCode);
                  setValue('gst_rate', record?.gst_rate ?? '', { shouldValidate: true, shouldDirty: true });
                  setDescription(record?.description || '');
                  setWarning(null);
                }}
                label="HSN Code *"
                error={errors.hsn_code}
                helperText={warningHelperText()}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name="gst_rate"
            control={control}
            render={({ field }) => (
              <InvTextField
                label="GST Rate"
                value={field.value !== '' && field.value != null ? `${field.value}%` : ''}
                placeholder="Auto-filled from HSN"
                disabled
                helperText="Derived from the selected HSN — not editable"
              />
            )}
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
}
